import React, { useEffect, useState, useMemo } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Typography,
  Paper,
  CircularProgress,
  Grid2,
  Button,
  Container,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import { useAuth } from "../../utils/auth";
import BuyProductsModal from "./BuyProductsModal";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Roboto Mono, monospace",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
};

const PaymentMethodsManager = ({
  paymentMethods,
  defaultPaymentMethodId,
  setDefaultPaymentMethodId,
  setPaymentMethods,
  stripeCustomerId,
  stripeAccountId,
  userEmail,
  setDefaultPaymentMethod,
  deletePaymentMethod,
}) => {
  const { createSetupIntent, getPaymentMethods } = useApi();
  const stripe = useStripe();
  const elements = useElements();
  const [adding, setAdding] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [error, setError] = useState();

  const handleAddPaymentMethod = async () => {
    if (!stripe || !elements) return;

    setAdding(true);
    setError(undefined);

    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error("Card element not ready");

      // 1) Create SetupIntent in correct account context
      const { client_secret: clientSecret } = await createSetupIntent({
        stripeCustomerId,
        stripeAccountId,
      });
      if (!clientSecret) throw new Error("Failed to create setup intent");

      // 2) Confirm (this SAVES/ATTACHES the PM to the customer)
      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card,
            billing_details: { email: userEmail },
          },
        }
      );
      if (setupError) throw new Error(setupError.message);

      const newPmId = setupIntent?.payment_method;
      if (!newPmId) throw new Error("Setup did not return a payment method");

      // 3) Optionally set default
      if (setAsDefault) {
        await setDefaultPaymentMethod({
          paymentMethodId: newPmId,
          stripeCustomerId,
          stripeAccountId,
        });
      }

      // 4) Refresh list from server (best)
      const pmResponse = await getPaymentMethods(
        stripeCustomerId,
        stripeAccountId
      );
      setPaymentMethods(pmResponse?.paymentMethods || []);
      setDefaultPaymentMethodId(pmResponse?.defaultPaymentMethodId);

      card.clear();
    } catch (err) {
      setError(err.message || "Failed to add payment method");
    } finally {
      setAdding(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await setDefaultPaymentMethod({
        paymentMethodId,
        stripeCustomerId,
        stripeAccountId,
      });
      setDefaultPaymentMethodId(paymentMethodId);
    } catch (err) {
      setError(err.message || "Failed to update default payment method");
    }
  };

  const handleDelete = async (paymentMethodId) => {
    if (
      paymentMethodId === defaultPaymentMethodId ||
      paymentMethods.length <= 1
    ) {
      return;
    }
    try {
      await deletePaymentMethod({
        paymentMethodId,
        stripeAccountId,
      });
      setPaymentMethods((prev) =>
        prev.filter((pm) => pm.id !== paymentMethodId)
      );
    } catch (err) {
      setError(err.message || "Failed to delete payment method");
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography sx={{ mb: 2 }}>
        <b>Payment Methods</b>
      </Typography>
      {paymentMethods.length === 0 ? (
        <Typography sx={{ mb: 2 }}>
          No saved payment methods. Add one below.
        </Typography>
      ) : (
        <RadioGroup
          value={defaultPaymentMethodId || ""}
          onChange={(e) => handleSetDefault(e.target.value)}
        >
          {paymentMethods.map((pm) => {
            const isDefault = pm.id === defaultPaymentMethodId;
            const canDelete = paymentMethods.length > 1 && !isDefault;
            return (
              <Box
                key={pm.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              >
                <FormControlLabel
                  value={pm.id}
                  control={<Radio />}
                  label={`${pm.card.brand.toUpperCase()} •••• ${
                    pm.card.last4
                  } Exp: ${pm.card.exp_month}/${pm.card.exp_year} ${
                    isDefault ? "(Default)" : ""
                  }`}
                />
                <Button
                  size="small"
                  color="error"
                  disabled={!canDelete}
                  onClick={() => handleDelete(pm.id)}
                >
                  Delete
                </Button>
              </Box>
            );
          })}
        </RadioGroup>
      )}
      <Divider sx={{ my: 2 }} />
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a new payment method
      </Typography>
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 2,
          mb: 1,
        }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={setAsDefault}
            onChange={() => setSetAsDefault((prev) => !prev)}
          />
        }
        label="Set as default"
      />
      <Box sx={{ mt: 1 }}>
        <LoadingButton
          variant="contained"
          onClick={handleAddPaymentMethod}
          loading={adding}
        >
          Add Payment Method
        </LoadingButton>
      </Box>
    </Paper>
  );
};

const Subscription = () => {
  const { stripeCustomerId, coachAccountId } = useUserInfo();
  const stripePromise = useMemo(() => {
    return loadStripe(
      import.meta.env.VITE_STRIPE_PUBLIC_KEY,
      coachAccountId ? { stripeAccount: coachAccountId } : undefined
    );
  }, [coachAccountId]);
  const {
    getSubscription,
    cancelSubscription,
    uncancelSubscription,
    getPaymentMethods,
    setDefaultPaymentMethod,
    deletePaymentMethod,
    getUserProducts,
  } = useApi();
  const [subscription, setSubscription] = useState();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState();
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [userProducts, setUserProducts] = useState([]);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const send = async () => {
      const sub = await getSubscription(stripeCustomerId, coachAccountId);
      const pmResponse = await getPaymentMethods(
        stripeCustomerId,
        coachAccountId
      );
      const userProducts = await getUserProducts(user.uid, "purchased");
      const assignedProducts = await getUserProducts(user.uid, "assigned");
      setUserSubscriptions(userProducts.filter((p) => p.recurring === true));
      setUserProducts(userProducts.filter((p) => p.recurring === false));
      setAssignedProducts(
        assignedProducts.filter((p) => p.recurring === false)
      );
      setSubscription(sub);
      setPaymentMethods(pmResponse?.paymentMethods || []);
      setDefaultPaymentMethodId(
        pmResponse?.defaultPaymentMethodId || undefined
      );
      setLoading(false);
    };
    if (stripeCustomerId) {
      send();
    }
  }, [stripeCustomerId, user]);

  const handleCancel = async () => {
    setLoading(true);
    const updatedSub = await cancelSubscription(subscription.id);
    setSubscription(updatedSub);
    setLoading(false);
  };

  const handleUncancel = async () => {
    setLoading(true);
    const updatedSub = await uncancelSubscription(subscription.id);
    setSubscription(updatedSub);
    setLoading(false);
  };

  if (!stripeCustomerId && !loading) {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Grid2 display="flex" justifyContent="center" sx={{ mt: 2 }}>
          <CircularProgress />
        </Grid2>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
        Subscription Details
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Typography sx={{ mb: 2 }}>
          <b>Active Subscriptions</b>
        </Typography>
        {userSubscriptions.length > 0 ? (
          userSubscriptions.map((product) => (
            <Grid2>
              <Typography sx={{ mb: 1 }}>
                {product.productName} - ${product.price.toFixed(2)}
              </Typography>
              {subscription.cancel_at ? (
                <LoadingButton
                  fullWidth
                  onClick={handleUncancel}
                  loading={loading}
                >
                  Uncancel Subscription
                </LoadingButton>
              ) : (
                <LoadingButton
                  fullWidth
                  onClick={handleCancel}
                  loading={loading}
                >
                  Cancel Subscription
                </LoadingButton>
              )}
            </Grid2>
          ))
        ) : (
          <Typography>No active subscriptions found.</Typography>
        )}
      </Paper>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography sx={{ mb: 2 }}>
          <b>Purchased Products</b>
        </Typography>
        {userProducts.length > 0 ? (
          userProducts.map((product) => (
            <Grid2>
              <Typography sx={{ mb: 1 }}>{product.productName}</Typography>
              <Grid2 container justifyContent="space-between">
                <Typography>
                  {product.priceDescription} - ${product.price.toFixed(2)}
                </Typography>
                <Typography>
                  {product.quantityLeft} session
                  {product.quantityLeft === 1 ? "" : "s"}
                  &nbsp;left
                </Typography>
              </Grid2>
            </Grid2>
          ))
        ) : (
          <Typography>No purchased products found.</Typography>
        )}
      </Paper>
      {assignedProducts.length > 0 && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography sx={{ mb: 2 }}>
            <b>Available Products for Purchase</b>
          </Typography>
          {assignedProducts.map((product) => (
            <Grid2 key={product.priceId}>
              <Typography sx={{ mb: 1 }}>{product.productName}</Typography>
              <Grid2 container justifyContent="space-between">
                <Typography>
                  {product.priceDescription} - ${product.price.toFixed(2)}
                </Typography>
                <Typography>
                  {product.quantityLeft} session
                  {product.quantityLeft === 1 ? "" : "s"}
                </Typography>
              </Grid2>
            </Grid2>
          ))}
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenModal(true)}
            sx={{ mt: 2 }}
          >
            Buy More Products
          </Button>
        </Paper>
      )}
      <Elements stripe={stripePromise}>
        <PaymentMethodsManager
          paymentMethods={paymentMethods}
          defaultPaymentMethodId={defaultPaymentMethodId}
          setDefaultPaymentMethodId={setDefaultPaymentMethodId}
          setPaymentMethods={setPaymentMethods}
          stripeCustomerId={stripeCustomerId}
          stripeAccountId={coachAccountId}
          userEmail={user.email}
          setDefaultPaymentMethod={setDefaultPaymentMethod}
          deletePaymentMethod={deletePaymentMethod}
        />
      </Elements>
      {/* Separate Elements provider for BuyProductsModal to avoid CardElement conflicts */}
      <Elements stripe={stripePromise}>
        <BuyProductsModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          assignedProducts={assignedProducts}
          userEmail={user.email}
          paymentMethodId={
            defaultPaymentMethodId || paymentMethods[0]?.id || undefined
          }
          hasSavedPaymentMethod={paymentMethods.length > 0}
          stripeCustomerId={stripeCustomerId}
          onSuccess={() => {
            window.location.reload();
          }}
        />
      </Elements>
    </Container>
  );
};

export default Subscription;
